import { useEffect, useState, useCallback, useMemo } from 'react';
import { placementLabel } from '@/lib/placementLabels';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, AlertCircle, BarChart3, Download,
  Loader2, Info, ArrowUpDown, CheckCircle2, CreditCard,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend,
} from 'recharts';

// ─── Types ──────────────────────────────────────────────────────────────────

interface RevenueCampaign {
  id: string;
  slug: string;
  brand_name: string;
  placement: string;
  is_house_ad: boolean;
  is_paused: boolean;
  cpm_rate_xcd: number | null;
  flat_fee_xcd: number | null;
  invoice_status: string;
  invoice_amount_xcd: number | null;
  paid_at: string | null;
  impressions: number;
  clicks: number;
  dismisses: number;
  conversions: number;
  revenue_estimated_xcd: number;
}

interface RevenuePlacement {
  placement: string;
  total_impressions: number;
  paid_impressions: number;
  paid_fill_rate_pct: number;
}

interface RevenueAdvertiser {
  brand_name: string;
  active_campaigns: number;
  revenue_invoiced_xcd: number;
  revenue_paid_xcd: number;
  revenue_outstanding_xcd: number;
}

interface RevenueTotals {
  total_impressions: number;
  total_clicks: number;
  total_dismisses: number;
  paid_impressions: number;
  paid_fill_rate_pct: number;
}

interface RevenueSummary {
  total_invoiced_xcd: number;
  total_paid_xcd: number;
  total_outstanding_xcd: number;
  overdue_count: number;
}

interface RevenueData {
  ok: boolean;
  date_from: string;
  date_to: string;
  campaigns: RevenueCampaign[];
  placements: RevenuePlacement[];
  advertisers: RevenueAdvertiser[];
  totals: RevenueTotals;
  revenue_summary: RevenueSummary;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatXCD(n: number | null | undefined): string {
  if (n == null) return 'XCD $0.00';
  return `XCD $${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '0';
  return Number(n).toLocaleString('en-US');
}

function formatPct(n: number | null | undefined): string {
  if (n == null) return '0.0%';
  return `${Number(n).toFixed(1)}%`;
}

function ctr(clicks: number, impressions: number): string {
  if (!impressions) return '0.0%';
  return `${((clicks / impressions) * 100).toFixed(2)}%`;
}

const INVOICE_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-gray-500/10', text: 'text-gray-400' },
  invoiced: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  overdue: { bg: 'bg-red-500/10', text: 'text-red-400' },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-500' },
};

function csvExport(headers: string[], rows: string[][], filename: string) {
  const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sort helpers ───────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';

function useSortable<T>(data: T[], defaultKey: keyof T, defaultDir: SortDir = 'desc') {
  const [sortKey, setSortKey] = useState<keyof T>(defaultKey);
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir);

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  const toggle = (key: keyof T) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return { sorted, sortKey, sortDir, toggle };
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function Revenue() {
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date range
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Mark paid dialog
  const [markPaidCampaign, setMarkPaidCampaign] = useState<RevenueCampaign | null>(null);
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_revenue_dashboard', {
        p_date_from: dateFrom,
        p_date_to: dateTo,
      });
      if (rpcError) throw rpcError;
      if (!rpcData?.ok) throw new Error(rpcData?.error || 'Revenue RPC returned ok=false');
      setData(rpcData as RevenueData);
    } catch (err: any) {
      setError(err.message || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mark paid handler
  const handleMarkPaid = async () => {
    if (!markPaidCampaign) return;
    setMarkPaidSubmitting(true);
    try {
      const amount = parseFloat(markPaidAmount) || markPaidCampaign.invoice_amount_xcd || null;
      const { data: paidData, error: rpcErr } = await supabase.rpc('mark_campaign_invoice_paid', {
        p_campaign_id: markPaidCampaign.id,
        p_amount_xcd: amount,
      });
      if (rpcErr) throw rpcErr;
      const paidResult = paidData as any;
      if (paidResult?.ok === false) throw new Error(paidResult.error || 'Failed to mark paid');
      toast.success(`Invoice for ${markPaidCampaign.brand_name} marked as paid`);
      setMarkPaidCampaign(null);
      // Optimistic update
      if (data) {
        setData({
          ...data,
          campaigns: data.campaigns.map(c =>
            c.id === markPaidCampaign.id
              ? { ...c, invoice_status: 'paid', paid_at: new Date().toISOString() }
              : c
          ),
        });
      }
      // Refetch for accurate totals
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark as paid');
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  // Sortable tables
  const campaignSort = useSortable(data?.campaigns || [], 'revenue_estimated_xcd');
  const placementSort = useSortable(data?.placements || [], 'paid_fill_rate_pct');
  const advertiserSort = useSortable(data?.advertisers || [], 'revenue_invoiced_xcd');

  // Inventory health check
  const lowFillPlacements = (data?.placements || []).filter(p => p.paid_fill_rate_pct < 50);

  // Chart data (headline bar for v1)
  const chartData = useMemo(() => {
    if (!data) return [];
    return [{
      name: `${data.date_from} – ${data.date_to}`,
      'Total Impressions': data.totals.total_impressions,
      'Paid Impressions': data.totals.paid_impressions,
    }];
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 bg-gray-800/50 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 bg-gray-800/50 rounded-xl" />
        <Skeleton className="h-96 bg-gray-800/50 rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
        <p className="font-medium">Failed to load revenue dashboard</p>
        <p className="text-sm mt-1">{error}</p>
        <Button variant="ghost" size="sm" className="mt-3 text-red-300" onClick={fetchData}>
          Retry
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const { totals, revenue_summary: rev } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Revenue</h1>
          <p className="text-sm text-gray-400 mt-1">Campaign performance and billing</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider px-1">From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-gray-900 border-gray-800 text-white text-sm w-36"
              />
            </div>
            <span className="text-gray-600 text-sm mt-4">→</span>
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider px-1">To</label>
              <Input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-gray-900 border-gray-800 text-white text-sm w-36"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Inventory health callout */}
      {lowFillPlacements.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-amber-300 font-medium">Unsold inventory detected</p>
              <p className="text-xs text-gray-400 mt-1">
                {lowFillPlacements.length} placement{lowFillPlacements.length !== 1 ? 's are' : ' is'} under 50% paid fill.
                You have unsold inventory. Consider lowering minimum bids or running a sales push.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 1 — KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total Invoiced"
          value={formatXCD(rev.total_invoiced_xcd)}
          icon={DollarSign}
          color="text-blue-400"
          bg="bg-blue-500/10"
          tooltip="Sum of invoice_amount_xcd for all campaigns in the selected period"
        />
        <KPICard
          label="Total Paid"
          value={formatXCD(rev.total_paid_xcd)}
          icon={CheckCircle2}
          color="text-emerald-400"
          bg="bg-emerald-500/10"
          tooltip="Sum of invoice_amount_xcd where invoice_status = 'paid'"
        />
        <KPICard
          label="Total Outstanding"
          value={formatXCD(rev.total_outstanding_xcd)}
          icon={CreditCard}
          color="text-amber-400"
          bg="bg-amber-500/10"
          tooltip="Invoiced minus paid. Includes overdue invoices."
          badge={rev.overdue_count > 0 ? `${rev.overdue_count} overdue` : undefined}
          badgeColor="bg-red-500/20 text-red-400"
        />
        <KPICard
          label="Paid Fill Rate"
          value={formatPct(totals.paid_fill_rate_pct)}
          icon={BarChart3}
          color="text-purple-400"
          bg="bg-purple-500/10"
          tooltip="Percentage of total impressions backed by paid (non-house) ads"
        />
      </div>

      {/* Row 2 — Impressions chart */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-white">Impressions Overview</h2>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm bg-blue-500/60" /> Total
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm bg-emerald-500/60" /> Paid
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barGap={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <RechartsTooltip
                contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }}
                labelStyle={{ color: '#9ca3af' }}
                itemStyle={{ color: '#e5e7eb' }}
                formatter={(v: number) => formatNumber(v)}
              />
              <Bar dataKey="Total Impressions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Paid Impressions" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Row 3 — Three tables */}
      <div className="space-y-6">
        {/* Campaigns table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-sm font-medium text-white">Campaigns</h2>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  const rows = campaignSort.sorted.map(c => [
                    c.brand_name, c.slug, c.placement, String(c.impressions), String(c.clicks),
                    ctr(c.clicks, c.impressions), String(c.revenue_estimated_xcd), c.invoice_status,
                  ]);
                  csvExport(['Brand', 'Slug', 'Placement', 'Impressions', 'Clicks', 'CTR', 'Revenue (XCD)', 'Invoice Status'], rows, 'campaigns.csv');
                  toast.success('Campaigns exported');
                }}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <SortHead label="Brand" sortKey="brand_name" sort={campaignSort} />
                  <TableHead className="text-gray-400">Slug</TableHead>
                  <TableHead className="text-gray-400">Placement</TableHead>
                  <SortHead label="Impressions" sortKey="impressions" sort={campaignSort} />
                  <SortHead label="Clicks" sortKey="clicks" sort={campaignSort} />
                  <TableHead className="text-gray-400">CTR</TableHead>
                  <SortHead label="Revenue" sortKey="revenue_estimated_xcd" sort={campaignSort} />
                  <TableHead className="text-gray-400">Invoice</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaignSort.sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-gray-500 py-8">
                      No campaign data for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  campaignSort.sorted.map((c) => {
                    const style = INVOICE_STATUS_STYLES[c.invoice_status] || INVOICE_STATUS_STYLES.pending;
                    return (
                      <TableRow
                        key={c.id}
                        className={`border-gray-800 hover:bg-gray-800/30 ${c.is_paused ? 'opacity-50' : ''}`}
                      >
                        <TableCell className="text-white font-medium">
                          {c.brand_name}
                          {c.is_house_ad && (
                            <Badge variant="outline" className="ml-2 text-[10px] border-gray-700 text-gray-500">House</Badge>
                          )}
                          {c.is_paused && (
                            <Badge className="ml-2 text-[10px] bg-gray-700/50 text-gray-400">PAUSED</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-400 text-xs font-mono">{c.slug}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs border-gray-700 text-gray-300">
                            {placementLabel(c.placement)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-300 tabular-nums">{formatNumber(c.impressions)}</TableCell>
                        <TableCell className="text-gray-300 tabular-nums">{formatNumber(c.clicks)}</TableCell>
                        <TableCell className="text-gray-300 tabular-nums">{ctr(c.clicks, c.impressions)}</TableCell>
                        <TableCell className="text-white font-medium tabular-nums">{formatXCD(c.revenue_estimated_xcd)}</TableCell>
                        <TableCell>
                          <Badge className={`${style.bg} ${style.text} border-0 text-xs`}>
                            {c.invoice_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {['invoiced', 'overdue'].includes(c.invoice_status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-400 hover:text-emerald-300 text-xs h-7 px-2"
                              onClick={() => {
                                setMarkPaidCampaign(c);
                                setMarkPaidAmount(String(c.invoice_amount_xcd || 0));
                              }}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Placements table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-sm font-medium text-white">Placements</h2>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  const rows = placementSort.sorted.map(p => [
                    p.placement, String(p.total_impressions), String(p.paid_impressions), `${p.paid_fill_rate_pct}%`,
                  ]);
                  csvExport(['Placement', 'Total Imps', 'Paid Imps', 'Fill Rate'], rows, 'placements.csv');
                  toast.success('Placements exported');
                }}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <SortHead label="Placement" sortKey="placement" sort={placementSort} />
                  <SortHead label="Total Imps" sortKey="total_impressions" sort={placementSort} />
                  <SortHead label="Paid Imps" sortKey="paid_impressions" sort={placementSort} />
                  <SortHead label="Fill Rate" sortKey="paid_fill_rate_pct" sort={placementSort} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {placementSort.sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                      No placement data
                    </TableCell>
                  </TableRow>
                ) : (
                  placementSort.sorted.map((p) => (
                    <TableRow
                      key={p.placement}
                      className={`border-gray-800 hover:bg-gray-800/30 ${p.paid_fill_rate_pct < 30 ? 'bg-amber-500/5' : ''}`}
                    >
                      <TableCell className="text-white font-medium">{p.placement.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-gray-300 tabular-nums">{formatNumber(p.total_impressions)}</TableCell>
                      <TableCell className="text-gray-300 tabular-nums">{formatNumber(p.paid_impressions)}</TableCell>
                      <TableCell>
                        <span className={`tabular-nums font-medium ${p.paid_fill_rate_pct < 30 ? 'text-amber-400' : p.paid_fill_rate_pct >= 70 ? 'text-emerald-400' : 'text-gray-300'}`}>
                          {formatPct(p.paid_fill_rate_pct)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Advertisers table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-sm font-medium text-white">Advertisers</h2>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  const rows = advertiserSort.sorted.map(a => [
                    a.brand_name, String(a.active_campaigns), String(a.revenue_invoiced_xcd),
                    String(a.revenue_paid_xcd), String(a.revenue_outstanding_xcd),
                  ]);
                  csvExport(['Brand', 'Active Campaigns', 'Invoiced (XCD)', 'Paid (XCD)', 'Outstanding (XCD)'], rows, 'advertisers.csv');
                  toast.success('Advertisers exported');
                }}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <SortHead label="Brand" sortKey="brand_name" sort={advertiserSort} />
                  <SortHead label="Active Campaigns" sortKey="active_campaigns" sort={advertiserSort} />
                  <SortHead label="Invoiced" sortKey="revenue_invoiced_xcd" sort={advertiserSort} />
                  <SortHead label="Paid" sortKey="revenue_paid_xcd" sort={advertiserSort} />
                  <SortHead label="Outstanding" sortKey="revenue_outstanding_xcd" sort={advertiserSort} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {advertiserSort.sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      No advertiser data
                    </TableCell>
                  </TableRow>
                ) : (
                  advertiserSort.sorted.map((a) => (
                    <TableRow key={a.brand_name} className="border-gray-800 hover:bg-gray-800/30">
                      <TableCell className="text-white font-medium">{a.brand_name}</TableCell>
                      <TableCell className="text-gray-300 tabular-nums">{a.active_campaigns}</TableCell>
                      <TableCell className="text-gray-300 tabular-nums">{formatXCD(a.revenue_invoiced_xcd)}</TableCell>
                      <TableCell className="text-emerald-400 tabular-nums">{formatXCD(a.revenue_paid_xcd)}</TableCell>
                      <TableCell className={`tabular-nums font-medium ${a.revenue_outstanding_xcd > 0 ? 'text-amber-400' : 'text-gray-300'}`}>
                        {formatXCD(a.revenue_outstanding_xcd)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Mark Paid Dialog */}
      <Dialog open={!!markPaidCampaign} onOpenChange={(o) => { if (!o) setMarkPaidCampaign(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
            <DialogDescription className="text-gray-400">
              Confirm payment for {markPaidCampaign?.brand_name} ({markPaidCampaign?.slug})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Amount Collected (XCD)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={markPaidAmount}
                onChange={(e) => setMarkPaidAmount(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500">
                Invoice amount: {formatXCD(markPaidCampaign?.invoice_amount_xcd)}. Override if the collected amount differs.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMarkPaidCampaign(null)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={markPaidSubmitting || !markPaidAmount}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {markPaidSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function KPICard({
  label, value, icon: Icon, color, bg, tooltip, badge, badgeColor,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  tooltip: string;
  badge?: string;
  badgeColor?: string;
}) {
  return (
    <Card className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-gray-600 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="bg-gray-800 border-gray-700 text-gray-200 text-xs max-w-[200px]">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-2xl font-bold text-white mt-1 tabular-nums">{value}</p>
            {badge && (
              <Badge className={`mt-1.5 text-[10px] border-0 ${badgeColor || 'bg-red-500/20 text-red-400'}`}>
                {badge}
              </Badge>
            )}
          </div>
          <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SortHead<T>({
  label, sortKey, sort,
}: {
  label: string;
  sortKey: keyof T;
  sort: { sortKey: keyof T; sortDir: SortDir; toggle: (k: keyof T) => void };
}) {
  const active = sort.sortKey === sortKey;
  return (
    <TableHead
      className="text-gray-400 cursor-pointer select-none hover:text-gray-200 transition-colors"
      onClick={() => sort.toggle(sortKey)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? 'text-emerald-400' : 'text-gray-600'}`} />
      </span>
    </TableHead>
  );
}
